// eslint-disable-next-line import/named
import { createRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import ErrorComponent from "./components/ErrorComponent";
import TicketView from "./view";

import PendingComponent from "../../../../components/PendingComponent";
import { SIDEBAR_STORAGE_NAME } from "../../../../components/sidebar/SidebarProvider";
import { getFolderBreadcrumbs } from "../../../../services/folders";
import {
  fetchTicketComments,
  // getFoldersAndTickets, //uncomment when we enable workflows
  getProject,
} from "../../../../services/projects";
import { isNil } from "../../../../utils/fp";
import { waitForState } from "../../../../utils/hooks";
import { setBreadcrumbs } from "../../../../utils/slices/punttProjects";
import {
  setComments,
  setLoading,
  setSelectedVersionIndex,
  setTicket,
} from "../../../../utils/slices/ticket";
import { setDrawerOpen } from "../../../../utils/slices/ui";
import { store } from "../../../../utils/store";
import { authLayoutRoute } from "../../../auth-layout/route";
import {
  isTicketShellHydrated,
  onTicketShellLeave,
} from "../../ticketShellLifecycle";
import { ticketCommentRoute } from "../ticket_comment";

const ticketShellRouteOptions = {
  gcTime: 0,
  // Ask Puntt parity: soft path changes between view ↔ comment deep link
  // must not force a full route reload / PendingComponent remount.
  shouldReload: false,
  pendingMs: 0,
  pendingMinMs: 500,
} as const;

const ticketSearchParser = z
  .object({
    version: z.coerce.number().min(0).optional(),
    revIndex: z.coerce.number().optional(),
    view: z.enum(["workflow", "grid", "list"]).optional(),
    rating: z.coerce.number().min(1).max(10).optional(),
    integration: z.coerce.boolean().optional(),
  })
  .transform((data) => {
    // I think `version` makes more sense than `revIndex` because we reverse the
    // index. We are transforming to keep some backwards compatibility for users
    // with share links that went to `revIndex`. Ideally, we could use the
    // pageId from TLDraw, but for now an index works and we can call it
    // `version`.

    if (!isNil(data.revIndex) && isNil(data.version)) {
      return {
        version: data.revIndex,
        revIndex: undefined,
        rating: data.rating,
        view: data.view,
        integration: data.integration,
      };
    }

    // Automatically convert "workflow" to "grid" for backwards compatibility
    if (data.view === "workflow") {
      data.view = "grid";
    }

    delete data.revIndex;
    return data;
  });

type TicketSearch = z.infer<typeof ticketSearchParser>;

export const query = "all" as const;

// why: setItem throws QuotaExceededError/SecurityError rather than no-oping when
// storage is full or blocked. onEnter runs on every navigation into the ticket
// route, so an unguarded write white-screens the page instead of only losing the
// remembered sidebar preference.
function writeLocalStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Preference only — never block navigation on a failed write.
  }
}

export const ticketRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  validateSearch(search: Record<string, unknown>) {
    return ticketSearchParser.parse(search);
  },
  beforeLoad({ params, location }) {
    const search = location.search as Record<string, unknown>;
    const commentId =
      typeof search.commentId === "string" ? search.commentId.trim() : "";
    const boardId =
      typeof search.boardId === "string" ? search.boardId.trim() : "";

    if (commentId && boardId) {
      throw redirect({
        to: ticketCommentRoute.to,
        params: {
          ticketId: params.ticketId,
          boardId,
          commentId,
        },
        replace: true,
      });
    }

    const parsed = ticketSearchParser.parse(search);
    // Ensure view, rating, and integration parameters are preserved during navigation
    return {
      search: {
        ...parsed,
        view: parsed.view,
        rating: parsed.rating,
        integration: parsed.integration,
      },
    };
  },
  path: "tickets/$ticketId/view",
  async loader({ params, context, location }) {
    const { version, view, rating, integration } =
      location.search as TicketSearch;

    const { ticketId } = params;
    const { queryClient } = context;
    // demo purposes only
    if (ticketId == "editabledoc" || ticketId == "editabledoc2") {
      return {};
    }

    // Soft re-enter from the comment deep-link sibling: keep TicketView mounted
    // (no loading spinner) and only sync search-driven version.
    const softShell = isTicketShellHydrated(ticketId);
    if (!softShell) {
      store.dispatch(setLoading(true));
    }
    // Fetch ticket before comments. Parallel Promise.all let a comments 404
    // (no share `t=`) race ahead of getProject's 403/409 share-link gate and
    // surface "Ticket Not Found" instead of the AI-blocked / join-confirm UI.
    const ticket = await queryClient.fetchQuery({
      queryKey: ["tickets", ticketId],
      queryFn: () => getProject(ticketId),
      staleTime: 1_000, // avoid double fetches when version is not specified; a bit naive
    });
    const comments = await queryClient.fetchQuery({
      queryKey: ["ticket-comments", ticketId, query],
      queryFn: () => fetchTicketComments({ ticketId, query }),
      staleTime: 1_000, // avoid double fetches when version is not specified; a bit naive
    });
    if (ticket.folder) {
      let breadcrumbs: Awaited<ReturnType<typeof getFolderBreadcrumbs>>;
      try {
        breadcrumbs = await queryClient.fetchQuery({
          queryKey: ["breadcrumbs", ticket.folder],
          queryFn: () => getFolderBreadcrumbs(ticket.folder as string),
        });
      } catch (error) {
        // In guest mode, we don't have access to breadcrumbs
        if (error instanceof Response && error.status === 401) {
          breadcrumbs = [];
        } else {
          throw error;
        }
      }
      store.dispatch(setBreadcrumbs(breadcrumbs));
    }

    // Sibling prev/next ticket list is disabled until workflows are re-enabled
    // for everyone (was previously filled from getFoldersAndTickets).
    const tickets = undefined;

    // TODO: Can we also move `store` into context?
    store.dispatch(setTicket(ticket));
    store.dispatch(setComments(comments));

    const rawSearch = location.search as Record<string, unknown>;
    const legacyCommentId =
      typeof rawSearch.commentId === "string" ? rawSearch.commentId.trim() : "";
    const legacyRevIndexRaw = rawSearch.revIndex ?? rawSearch.version;
    if (
      legacyCommentId &&
      legacyRevIndexRaw != null &&
      legacyRevIndexRaw !== ""
    ) {
      const revIndex = Number(legacyRevIndexRaw);
      if (!Number.isNaN(revIndex) && ticket.revisionBoards?.length) {
        const sortedBoards = [...ticket.revisionBoards].sort(
          (a, b) =>
            new Date(a.createdAt as unknown as Date).getTime() -
            new Date(b.createdAt as unknown as Date).getTime(),
        );
        const board = sortedBoards[revIndex];
        const boardId = board?._id?.toString();
        if (boardId) {
          throw redirect({
            to: ticketCommentRoute.to,
            params: {
              ticketId,
              boardId,
              commentId: legacyCommentId,
            },
            replace: true,
          });
        }
      }
    }

    // We do not enforce a version to be specified upon entry. Ideally,
    // when the page loads, we just kick you to the latest version. However, if
    // you've specified a version index in the URL search params, that should
    // take precedence and this won't override that.
    if (isNil(version) && ticket.revisionBoards.length > 0) {
      if (view === "workflow") {
        throw redirect({
          to: ticketRoute.to,
          params: { ticketId },
          replace: true,
          search: {
            version: ticket.revisionBoards.length - 1,
            view: "grid",
            rating: rating,
            integration: integration,
          },
        });
      } else {
        throw redirect({
          to: ticketRoute.to,
          params: { ticketId },
          replace: true,
          search: {
            version: ticket.revisionBoards.length - 1,
            rating: rating,
            integration: integration,
          },
        });
      }
    }

    // By this point, it is not possible for `version` to be undefined.
    store.dispatch(setSelectedVersionIndex(version!));

    // wait to render until redux has finished adding to the store
    await waitForState(store, (state) => !isNil(state.ticket.value));
    store.dispatch(setLoading(false));

    return {
      tickets,
      rating,
      integration,
    };
  },
  onLeave({ params, context }) {
    const { queryClient } = context;
    const { ticketId } = params;

    // TODO: Can we also move `store` into context?
    // Soft leave when navigating to the same ticket's comment deep link.
    onTicketShellLeave({ ticketId, queryClient });
  },
  onEnter() {
    writeLocalStorage(`app_${SIDEBAR_STORAGE_NAME}`, "false");
    store.dispatch(setDrawerOpen({ app: false }));
  },
  component: TicketView,
  pendingComponent: PendingComponent,
  errorComponent: ErrorComponent,
  ...ticketShellRouteOptions,
});
